'use client';

import { Card, Image, Link, Text } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';

import AddToCart from '@features/marketplace/catalog/AddToCart';
import { useOverridesContext } from '@features/marketplace/catalog/context';
import { ProductDetails } from 'helpers/fakeData';

const getProductPath = (product: ProductDetails) => {
  switch (product.type) {
    case 'charge-station':
      return `/marketplace/charge-stations/${product.id}`;
    case 'powerbank':
      return `/marketplace/powerbanks/${product.id}`;
    default:
      return undefined;
  }
};

interface Props {
  item: ProductDetails;
  hideAddToCart?: boolean;
}

const CatalogItem = ({ item, hideAddToCart }: Props) => {
  const router = useRouter();
  const [overrides] = useOverridesContext();

  const product = useMemo(() => {
    if (overrides.product != null) {
      return overrides.product;
    }

    return item;
  }, [item, overrides.product]);

  const onClick = useCallback(() => {
    const path = getProductPath(product);
    if (path != null) {
      router.push(path);
    }
  }, [product, router]);

  useEffect(() => {
    const path = getProductPath(product);
    if (path != null) {
      router.prefetch(path);
    }
  }, [product, router]);

  return (
    <Card.Root overflow="hidden" bg="surface.background" borderColor="border.light" h="fit-content">
      <Card.Body gap="2">
        <Image
          cursor="pointer"
          onClick={onClick}
          h="2xs"
          src={product.image}
          alt={product.name}
          fit="contain"
        />
        <Card.Title>
          <Link variant="underline" onClick={onClick}>
            <Text lineClamp={1} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
              {product.name}
            </Text>
          </Link>
        </Card.Title>
        <Text textStyle="2xl" fontWeight="medium" letterSpacing="tight" mt="2">
          {product.currency}
          {product.price}
        </Text>
      </Card.Body>
      {!hideAddToCart && (
        <Card.Footer gap="2">
          <AddToCart product={product} />
        </Card.Footer>
      )}
    </Card.Root>
  );
};

export default observer(CatalogItem);
